if (!customElements.get('product-manager')) {
	class ProductManager extends HTMLElement {
		static get events() {
			return window.UFT?.EventManager.eventStrings;
		}
		static UrlParams = {
			SectionId: 'section_id',
			Variant: 'variant',
			SellingPlan: 'selling_plan',
		};

		static EventSource = {
			SellingPlanChange: 'selling-plan-change',
			VariantChange: 'variant-change',
		};

		static debug = true;

		constructor() {
			super();
			this.renderer = new CacheableRenderer();
			this.state = {};
		}

		connectedCallback() {
			try {
				this.#log(`[ProductManager] connectedCallback`);
				this.#initializeState();
				this.#subscribeToEvents();
				this.#cacheInitialState();
				this.#dispatchProductViewedEvent();
			} catch (error) {
				console.error('[ProductManager] Initialization failed:', error);
			}
		}

		disconnectedCallback() {
			this.#unsubscribeFromEvents();
		}

		#initializeState() {
			const scriptTag = this.querySelector('#product-data');
			if (!scriptTag) {
				throw new Error('ProductManager requires #product-data script tag');
			}

			try {
				this.state = JSON.parse(scriptTag.textContent);

				if (this.dataset.productUrl) {
					this.state.product.url = this.dataset.productUrl;
				}
			} catch (error) {
				throw new Error(`Failed to parse product data: ${error.message}`);
			}
		}

		#subscribeToEvents() {
			this.#validateEventManager();

			this.eventUnsubscribers = [
				window.UFT.EventManager.on(
					ProductManager.events.PRODUCT_SELLING_PLAN_CHANGE,
					this.#onSellingPlanChange.bind(this)
				),
				window.UFT.EventManager.on(ProductManager.events.PRODUCT_VARIANT_CHANGE, this.#onVariantChange.bind(this)),
				window.UFT.EventManager.on(ProductManager.events.PRODUCT_HTML_UPDATED, this.#onHtmlUpdated.bind(this)),
			];
		}

		#unsubscribeFromEvents() {
			this.eventUnsubscribers?.forEach((unsubscribe) => unsubscribe());
		}

		#validateEventManager() {
			if (!window.UFT?.EventManager) {
				throw new Error('UFT EventManager not available');
			}
		}

		#onSellingPlanChange(detail) {
			this.#log('Selling plan change', detail);
			try {
				this.#updateSellingPlan(detail?.sellingPlan);
				this.#getAndDispatchProductHtml(ProductManager.EventSource.SellingPlanChange);
			} catch (error) {
				console.warn('[ProductManager] Selling plan change failed:', error.message);
			}
		}

		#onVariantChange(detail) {
			this.#log('Variant change', detail);
			try {
				this.#validateVariantChangeDetail(detail);
				this.#updateVariant(detail.id);

				if (detail.sellingPlan !== undefined) {
					this.#updateSellingPlan(detail.sellingPlan);
				}

				this.#getAndDispatchProductHtml(ProductManager.EventSource.VariantChange);
				this.#dispatchProductViewedEvent();
			} catch (error) {
				console.warn('[ProductManager] Variant change failed:', error.message);
			}
		}

		#onHtmlUpdated({ html }) {
			const standaloneSelectors = [
				'.product__price',
				'.accordion--details .product__details-table',
				'.in-stock-notification__wrapper',
				'.discount-notice__wrapper',
				'.product__blocks .product-disclaimer__container',
				'product-form',
			];
			CacheableRenderer.rerenderComponents(html, standaloneSelectors);
		}

		#updateSellingPlan(sellingPlan) {
			this.#log('Updating selling plan', sellingPlan);
			this.state.sellingPlan = sellingPlan;
		}

		#updateVariant(variantId) {
			this.#log('Updating variant', variantId);
			const newVariant = this.#findVariantById(variantId);
			if (!newVariant) {
				throw new Error(`Variant not found: ${variantId}`);
			}

			this.state.variant = newVariant;
			this.#maintainSellingPlanCompatibility();
			this.#log('Updated to variant:', newVariant.title);
		}

		#maintainSellingPlanCompatibility() {
			if (!this.state.sellingPlan?.id) {
				return;
			}

			const isCompatible = this.#isSellingPlanCompatibleWithVariant(this.state.sellingPlan, this.state.variant);
			if (isCompatible) {
				this.#log('Selling plan remains compatible');
			} else {
				this.#log('Selling plan incompatible, clearing');
				this.state.sellingPlan = null;
			}
		}

		#isSellingPlanCompatibleWithVariant(sellingPlan, variant) {
			return variant.selling_plan_allocations?.some(
				(allocation) => allocation.selling_plan_id === Number(sellingPlan.id)
			);
		}

		#findVariantById(variantId) {
			return this.state.product?.variants?.find((v) => v.id == variantId);
		}

		#validateVariantChangeDetail(detail) {
			if (!detail?.id) {
				throw new Error('No variant ID in event detail');
			}
		}

		async #getAndDispatchProductHtml(source) {
			try {
				const url = this.#buildSectionUrl();
				const html = await this.renderer.getHtml(url);
				window.UFT.EventManager.dispatch(ProductManager.events.PRODUCT_HTML_UPDATED, {
					html,
					source,
				});
			} catch (error) {
				console.warn('[ProductManager] Product HTML get and dispatch failed:', error.message);
			}
		}

		#buildSectionUrl() {
			this.#validateUrlRequirements();

			const params = new URLSearchParams({
				[ProductManager.UrlParams.SectionId]: this.state.sectionId,
				[ProductManager.UrlParams.Variant]: this.state.variant.id,
			});

			if (this.state.sellingPlan?.id) {
				params.set(ProductManager.UrlParams.SellingPlan, this.state.sellingPlan.id);
			}

			return `${this.state.product.url}?${params.toString()}`;
		}

		#validateUrlRequirements() {
			const { product, variant, sectionId } = this.state;

			if (!product?.url || !sectionId || !variant?.id) {
				throw new Error('Missing required URL parameters');
			}
		}

		#cacheInitialState() {
			try {
				const currentUrl = this.#buildSectionUrl();
				this.renderer.setCache(currentUrl, this.outerHTML);
				this.#log('Cached initial state for url:', currentUrl);
			} catch (error) {
				console.warn('[ProductManager] Failed to cache initial state:', error.message);
			}
		}

		#dispatchProductViewedEvent() {
			try {
				this.#log(`[ProductManager] dispatchProductViewedEvent`);
				this.#validateEventManager();

				this.#log(`[ProductManager] dispatchProductViewedEvent, validateEventManager has succeeded.`);
				const eventData = {
					productId: this.state.product?.id,
					variantId: this.state.variant?.id,
					productName: this.state.product?.title,
					productType: this.state.product?.type,
					productUrl: this.state.product?.url,
					variantTitle: this.state.variant?.title,
					price: this.state.variant?.price,
					compareAtPrice: this.state.variant?.compare_at_price,
					available: this.state.variant?.available,
					sku: this.state.variant?.sku,
					discount: this.#calculatePriceDiscount(),
				};

				window.UFT.EventManager.dispatch(ProductManager.events.PRODUCT_VIEWED, eventData);
				this.#log('Dispatched product viewed event', eventData);
			} catch (error) {
				console.warn('[ProductManager] Failed to dispatch product viewed event:', error.message);
			}
		}

		/**
		 * Get discount by reading value from hidden data element
		 *
		 * @returns {number|null} Discount amount in dollars, 0 if no discount, null on error
		 */
		#calculatePriceDiscount() {
			try {
				const discountElement = this.querySelector('.price__discount-data');

				if (!discountElement) {
					this.#log('No discount element found, discount is 0');
					return 0;
				}

				const discountValue = parseFloat(discountElement.dataset.discount);

				if (isNaN(discountValue)) {
					console.warn('[ProductManager] Invalid discount value in data-discount attribute');
					return null;
				}

				this.#log(`Discount from data element: $${discountValue.toFixed(2)}`);
				return parseFloat(discountValue.toFixed(2));
			} catch (error) {
				console.warn('[ProductManager] Failed to get discount:', error.message);
				return null;
			}
		}

		#log(...args) {
			if (ProductManager.debug) {
				console.log('[ProductManager]', ...args);
			}
		}
	}

	customElements.define('product-manager', ProductManager);
}
